import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQuery } from '../base/baseQuery'

// Drops undefined/empty-string keys before a request is sent. The backend's
// global ValidationPipe runs with `forbidNonWhitelisted: true`, so it 400s on
// unexpected keys, and optional numeric fields (e.g. `capacity`) fail
// `@IsInt()` if sent as `''` rather than simply omitted.
const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== null),
  )

export const groupServiceApi = createApi({
  reducerPath: 'groupServiceApi',
  baseQuery,
  tagTypes: ['Group', 'GroupStudent'],
  endpoints: (builder) => ({
    // ---- Groups CRUD ----
    getGroups: builder.query({
      query: (params) => ({ url: '/backoffice/groups', params: cleanParams(params) }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Group', id })),
              { type: 'Group', id: 'LIST' },
            ]
          : [{ type: 'Group', id: 'LIST' }],
    }),
    getGroupById: builder.query({
      query: (id) => `/backoffice/groups/${id}`,
      providesTags: (result, error, id) => [{ type: 'Group', id }],
    }),
    addGroup: builder.mutation({
      query: (body) => ({ url: '/backoffice/groups', method: 'POST', body }),
      invalidatesTags: [{ type: 'Group', id: 'LIST' }],
    }),
    updateGroup: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/backoffice/groups/${id}`, method: 'PUT', body }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Group', id },
        { type: 'Group', id: 'LIST' },
      ],
    }),
    deleteGroup: builder.mutation({
      query: ({ id, force }) => ({
        url: `/backoffice/groups/${id}`,
        method: 'DELETE',
        params: cleanParams({ force }),
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Group', id },
        { type: 'Group', id: 'LIST' },
      ],
    }),

    // ---- Student assignment ----
    getGroupStudents: builder.query({
      query: ({ groupId, ...params }) => ({
        url: `/backoffice/groups/${groupId}/students`,
        params: cleanParams(params),
      }),
      providesTags: (result, error, { groupId }) => [
        { type: 'GroupStudent', id: groupId },
        { type: 'GroupStudent', id: 'LIST' },
      ],
    }),
    assignStudent: builder.mutation({
      query: ({ groupId, student_id }) => ({
        url: `/backoffice/groups/${groupId}/students`,
        method: 'POST',
        body: { student_id },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'GroupStudent', id: groupId },
        { type: 'Group', id: groupId },
        { type: 'Group', id: 'LIST' },
      ],
    }),
    assignStudents: builder.mutation({
      query: ({ groupId, student_ids }) => ({
        url: `/backoffice/groups/${groupId}/students/bulk`,
        method: 'POST',
        body: { student_ids },
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'GroupStudent', id: groupId },
        { type: 'Group', id: groupId },
        { type: 'Group', id: 'LIST' },
      ],
    }),
    unassignStudent: builder.mutation({
      query: ({ groupId, studentId }) => ({
        url: `/backoffice/groups/${groupId}/students/${studentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'GroupStudent', id: groupId },
        { type: 'Group', id: groupId },
        { type: 'Group', id: 'LIST' },
      ],
    }),
    moveStudent: builder.mutation({
      query: ({ student_id, target_group_id }) => ({
        url: '/backoffice/groups/students/move',
        method: 'POST',
        body: { student_id, target_group_id },
      }),
      // The moved student leaves one group's roster and joins another; the
      // caller (a group-details page) only reliably knows the destination id,
      // so every open student list is refreshed via the shared 'LIST' tag.
      invalidatesTags: [{ type: 'GroupStudent', id: 'LIST' }, { type: 'Group', id: 'LIST' }],
    }),
    getStudentGroups: builder.query({
      query: (studentId) => `/backoffice/students/${studentId}/groups`,
    }),
  }),
})

export const {
  useGetGroupsQuery,
  useGetGroupByIdQuery,
  useAddGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useGetGroupStudentsQuery,
  useAssignStudentMutation,
  useAssignStudentsMutation,
  useUnassignStudentMutation,
  useMoveStudentMutation,
  useGetStudentGroupsQuery,
} = groupServiceApi
